/* eslint-disable import/no-extraneous-dependencies */
import 'babel-polyfill';
import 'isomorphic-unfetch';
import React from 'react';
import ReactDOM from 'react-dom';
import {
  ApolloClient,
  InMemoryCache,
  createHttpLink,
  ApolloProvider as ApolloHooksProvider,
  ApolloLink,
  from,
} from '@apollo/client';
import { GlobalizationProvider, currentCountry, currentLang } from '@rcg/ui-react';
import { setContext } from '@apollo/client/link/context';
import { applyPolyfills, defineCustomElements } from '@rcg/royal-payway/loader';
import { defineCustomElements as globalLoginDefineCustomElements } from '@rccl/global-login/loader';
import lscache from 'lscache';
import { persistCache } from 'apollo3-cache-persist';
import defaults from './graphql/defaults';
import resolvers from './graphql/resolvers';
import typeDefs from './graphql/typeDefs';
import Theme from './theme';
import App from './App';
import GlobalLoginProvider from './providers/GlobalLoginProvider';
import {
  BOOKING_TOKEN,
  COOKIE_KEYS,
  InitiatedFromEmail,
  LOCAL_GRAPH_URI,
} from './commons/constants';
import { IS_LOGGED_IN } from './graphql/queries';
import { LDUser } from './commons/ld-user';
import { generateRelativeUrl, getCookies, safeDecode } from './commons/utils';

import './styles.css';

const cacheFlushed = lscache.get('flushed');
if (!cacheFlushed) {
  lscache.flush();
  lscache.set('flushed', true);
}

const cache = new InMemoryCache();

const httpLink = createHttpLink({
  uri: generateRelativeUrl(process.env.REACT_APP_GRAPHQL_URI) || LOCAL_GRAPH_URI,
});

const authLink = setContext((_, { headers }) => {
  const guestAcctAccessToken = getCookies(COOKIE_KEYS.ACCESS_TOKEN);
  // get the authentication token from local storage if it exists
  const token = sessionStorage.getItem(BOOKING_TOKEN);
  return {
    headers: {
      ...headers,
      'x-booking-token': token,
      ...(guestAcctAccessToken && {
        'x-guest-acct-access-token': guestAcctAccessToken,
      }),
      'apollographql-client-name': 'Booking Amend',
      'apollographql-query-name': _.operationName,
      'apollographql-client-version': '1.11.0',
      country: currentCountry().toUpperCase(),
      language: currentLang(),
      'x-session-id': LDUser(),
    },
  };
});

const eslEnvLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    sessionStorage.removeItem('eslEnv');
    const context = operation.getContext();
    const {
      response: { headers },
    } = context;
    if (headers) {
      const eslEnvHeader = headers.get('esl-env');
      if (eslEnvHeader && eslEnvHeader !== null) {
        sessionStorage.setItem('eslEnv', eslEnvHeader);
      }
    }
    return response;
  });
});
let client;
persistCache({
  cache,
  storage: localStorage,
}).then(() => {
  client = new ApolloClient({
    cache,
    link: from([authLink, eslEnvLink, httpLink]),
    clientState: {
      defaults,
      resolvers,
      typeDefs,
    },
    resolvers: {},
  });

  const urlParams = window.location.search;
  const params = urlParams.match(/token=([^&]+)/i);
  if (Array.isArray(params) && params[1]) {
    sessionStorage.setItem(BOOKING_TOKEN, safeDecode(params[1]));
    sessionStorage.setItem(InitiatedFromEmail, true);
  }
  cache.writeQuery({
    query: IS_LOGGED_IN,
    data: {
      isLoggedIn: !!sessionStorage.getItem(BOOKING_TOKEN),
    },
  });

  const AEM_SERVICE_URL = process.env.REACT_APP_AEM_SERVICE_URL;
  const DICTIONARY_NAME = process.env.REACT_APP_DICTIONARY_NAME;
  const AEM_SERVICE_CACHE_TIME = process.env.REACT_APP_AEM_SERVICE_CACHE_TIME;
  const cacheTime = AEM_SERVICE_CACHE_TIME && Number(AEM_SERVICE_CACHE_TIME);

  ReactDOM.render(
    <GlobalizationProvider url={AEM_SERVICE_URL} cacheTime={cacheTime} dicName={DICTIONARY_NAME}>
      <ApolloHooksProvider client={client}>
        <GlobalLoginProvider>
          <Theme>
            <App />
          </Theme>
        </GlobalLoginProvider>
      </ApolloHooksProvider>
    </GlobalizationProvider>,
    document.getElementById('react-root'),
  );
});

/*
  Royal payway implementation
  More info: https://confluence.rccl.com/pages/viewpage.action?spaceKey=PAYM&title=Web+Environment
  3DS Component: https://confluence.rccl.com/display/PAYM/3DS+Component
*/
applyPolyfills().then(() => {
  defineCustomElements();
});

globalLoginDefineCustomElements(window);
